<template>
  <TemplatePage
    :seo-title="get_seoPage.title"
    :seo-description="get_seoPage.description"
    twitter-img-url="/img/seo/lobby/vendor_1200_1200.jpg"
    og-img-url="/img/seo/lobby/vendor_1200_630.jpg"
    :is-bread-top="false"
    :is-error="isErrorPage"
    class="template-providers"
  >
    <div class="template-providers__actions-desktop">
      <FilterButton v-show="isShowFilterButton" :hidden-text-mob="false" />
    </div>

    <div class="template-providers__content">
      <SkeletonHeadProvider v-if="get_providerItem.isLoading && !get_providerItem.data" />

      <HeadlineProvider
        v-else
        key="header"
        :data="get_providerItem.data"
        class="template-providers__info"
      >
        <NavProviders key="tabs" class="template-providers__nav" />
      </HeadlineProvider>

      <div class="template-providers__actions-mobile">
        <FilterButton
          v-show="isShowFilterButton"
          :hidden-text-mob="false"
        />
      </div>

      <div class="template-providers__widgets-wrap">
        <template v-if="isLayoutDefault">
          <div
            v-if="!get_providerItem.isLoading && get_providerItem.data"
            key="ProviderWidgetWrap"
            class="template-providers__widgets"
          >
            <ProviderWidget
              v-for="(widget, index) in get_providerItem.data.layout"
              :key="widget.nameUa"
              :widget="widget"
              :index="index"
              :term="get_providerItem.data.term"
            />
          </div>

          <div v-else class="template-providers__skeleton">
            <SkeletonWidgets :columns="6" :rows="1" />
            <SkeletonWidgets :columns="6" :rows="1" />
          </div>
        </template>

        <template v-else-if="isLayoutList">
          <ExpandList
            v-if="!get_providerItem.isLoading && isLayoutListData"
            :key="$route.params[$options.static.PARAM_SUB_PAGE]"
            :query-key="$route.params[$options.static.PARAM_SUB_PAGE]"
            :fetch-method="fetch_providerItem"
            :param-page="$route.params[$options.static.PARAM_SUB_PAGE]"
            :list-type="$options.static.LIST_TYPE.GAME.NAME"
            is-auto-pagination
            :prop-data="get_providerItem.data"
            class="template-providers__widget-list"
          />

          <SkeletonExpandList v-else />
        </template>
      </div>
    </div>
  </TemplatePage>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ExpandList from '~/components/Lobby/ExpandList'
import NavProviders from '~/components/Lobby/ViewsProvider/NavProviders'
import HeadlineProvider from '~/components/Lobby/ViewsProvider/HeadlineProvider'
import ProviderWidget from '~/components/Lobby/ViewsProvider/ProviderWidget'
import { PARAM_SUB_PAGE, LIST_TYPE, DISPLAY_MODE, QUERY } from '@/components/Lobby/enum.js'
import SkeletonExpandList from '~/components/Lobby/Skeletons/SkeletonExpandList'
import SkeletonWidgets from '~/components/shared/Skeletons/SkeletonWidgets'
import TemplatePage from '~/components/Base/TemplatePage'
import FilterButton from '~/components/Lobby/FilterButton'
import SkeletonHeadProvider from '~/components/Lobby/Skeletons/SkeletonHeadProvider'

export default {
  name: 'TemplateProviders',
  components: {
    ExpandList,
    NavProviders,
    HeadlineProvider,
    ProviderWidget,
    SkeletonExpandList,
    SkeletonWidgets,
    TemplatePage,
    FilterButton,
    SkeletonHeadProvider,
  },
  static: {
    PARAM_SUB_PAGE,
    LIST_TYPE,
  },
  async fetch() {
    await this.fetchProviderItem()

    const data = this.get_providerItem.data

    if (!data) {
      return
    }
  },
  head() {
    const isHasQueryPage = this.$route.query[`${QUERY.PAGE}_${this.$route.params[PARAM_SUB_PAGE]}`]
    if (this.isLayoutList && isHasQueryPage) {
      return {
        meta: [{ name: 'robots', content: 'noindex, follow' }],
      }
    }
  },
  computed: {
    ...mapGetters('lobby', ['get_providerItem']),
    ...mapGetters('seo', ['get_seoPage']),
    queryLayout() {
      return this.$route.query[QUERY.DISPLAY_MODE]
    },
    isLayoutDefault() {
      return !this.$route.query[QUERY.DISPLAY_MODE]
    },
    isLayoutList() {
      return this.queryLayout === DISPLAY_MODE.LIST
    },
    isLayoutListData() {
      return this.get_providerItem.data?.games?.result?.length
    },
    isErrorPage() {
      const data = this.get_providerItem.data
      const isIncorrectData = data && data.term !== this.$route.params[PARAM_SUB_PAGE]
      return (!data && this.get_providerItem.error) || isIncorrectData
    },
    isShowFilterButton() {
      const isSubPage = this.$route.params[PARAM_SUB_PAGE]
      const isDisplayModeList = this.$route.query[QUERY.DISPLAY_MODE] === DISPLAY_MODE.LIST

      return isSubPage && isDisplayModeList
    },
  },
  watch: {
    queryLayout() {
      this.fetchProviderItem()
    },
  },
  destroyed() {
    this.reset_providerItem()
  },
  methods: {
    ...mapActions('lobby', [
      'fetch_providerItem',
      'reset_providerItem',
    ]),
    async fetchProviderItem() {
      await this.fetch_providerItem({
        term: this.$route.params[PARAM_SUB_PAGE],
        displayMode: this.isLayoutDefault ? DISPLAY_MODE.BY_LAYOUT : DISPLAY_MODE.LIST,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.template-providers {
  padding-top: 0!important;

  &__actions-mobile {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 8px;

    @include media(tablet-album) {
      display: none;
    }
  }

  &__actions-desktop {
    display: none;

    @include media(tablet-album) {
      display: flex;
      justify-content: flex-end;
      height: 44px;
      margin-bottom: 24px;
    }
  }

  &__content {
    @include media(tablet-album) {
      display: flex;
      align-items: flex-start;
    }
  }

  &__widgets-wrap {
    @include media(tablet-album) {
      width: calc(100% - 308px - var(--spaces-xl));
      overflow: hidden;
    }
  }

  &__info {
    margin-bottom: var(--spaces-s);

    @include media(tablet-album) {
      width: calc(308px + var(--spaces-xl));
      margin-right: var(--spaces-xl);
    }
  }

  &__widgets {
    ::v-deep > *:not(:last-child) {
      margin-bottom: var(--spaces-2xl);
    }
  }

  &__skeleton {
    display: flex;
    flex-direction: column;
    column-gap: 32px;
  }

  ::v-deep {
    ._CardGame {
      width: 122px;

      @include media(mobile-md) {
        width: 142px;
      }

      @include media(tablet) {
        width: 162px;
      }

      @include media(tablet-album) {
        width: 170px;
      }
    }

    .list-games__list.list-games__list {
      grid-template-columns: repeat(auto-fill, minmax(122px, 1fr));
      justify-content: flex-start;

      @include media(mobile-md) {
        grid-template-columns: repeat(auto-fill, minmax(142px, 1fr));
      }

      @include media(tablet) {
        grid-template-columns: repeat(auto-fill, minmax(162px, 1fr));
        gap: var(--spaces-l);
      }

      @include media(tablet-album) {
        grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
      }
    }
  }

  &__widget-list {
    ::v-deep {
      .list-games__list.list-games__list {
        grid-template-columns: repeat(2, 1fr);

        @include media(mobile-md) {
          grid-template-columns: repeat(3, 1fr);
        }

        @include media(tablet) {
          grid-template-columns: repeat(4, 1fr);
        }

        @include media(tablet-album) {
          grid-template-columns: repeat(5, 1fr);
        }
      }
    }
  }
}
</style>
